import type { InsertToPosition } from '@blocksuite/affine-shared/utils';
import {
  computed,
  type ReadonlySignal,
  type Signal,
  signal,
} from '@preact/signals-core';

import type {
  ReorderGeometry,
  ReorderHost,
  ReorderState,
  StartReorderOpts,
} from './types.js';

interface GhostPosition {
  readonly clientX: number;
  readonly clientY: number;
}

/**
 * View-agnostic row-reorder state machine. A view calls {@link start}
 * on pointer-down; the controller captures the pointer, keeps a signal
 * for the current {@link ReorderState}, and on pointer-up commits the
 * move via {@link ReorderHost.moveRows}. Selection is read only —
 * views manage their own selection store.
 */
export class LivePreviewReorderController {
  private readonly _state: Signal<ReorderState | null> = signal(null);

  private readonly _ghost: Signal<GhostPosition | null> = signal(null);

  /**
   * Geometry of the current session, captured on `start`. Stored as a
   * signal so {@link liveShifts$} re-derives on every start/teardown.
   * Views build their geometry against a pointer-down snapshot (row
   * rects, column rects) so mid-drag re-measurements don't create a
   * feedback loop with the applied `translateY`.
   */
  private readonly _geometry: Signal<ReorderGeometry | null> = signal(null);

  private cleanup: (() => void) | null = null;

  readonly state$: ReadonlySignal<ReorderState | null> = this._state;

  readonly ghostPosition$: ReadonlySignal<GhostPosition | null> = this._ghost;

  /**
   * Per-row `{dx, dy}` offsets for the current state. Consumers (row
   * renderers) read this signal during render to apply `translate` and
   * let every selected row glide to its post-drop slot. Empty when no
   * reorder is active or the geometry does not implement
   * {@link ReorderGeometry.getLiveShift}.
   */
  readonly liveShifts$: ReadonlySignal<
    ReadonlyMap<string, { dx: number; dy: number }>
  > = computed(() => {
    const state = this._state.value;
    const geom = this._geometry.value;
    if (!state || !geom?.getLiveShift) return new Map();
    return geom.getLiveShift(state);
  });

  get isActive(): boolean {
    return this._state.value !== null;
  }

  start(opts: StartReorderOpts): void {
    if (this.cleanup) this.cleanup();

    const {
      seedRowId,
      selectedIds,
      pointer,
      geometry,
      host,
      captureTarget,
      onCommit,
      onCancel,
    } = opts;

    const draggedIds = this.resolveDraggedIds(seedRowId, selectedIds);
    this._geometry.value = geometry;
    this._state.value = {
      draggedIds,
      targetIndex: null,
      dropScope: undefined,
    };
    this._ghost.value = { clientX: pointer.clientX, clientY: pointer.clientY };

    try {
      captureTarget?.setPointerCapture(pointer.pointerId);
    } catch {
      // captureTarget may already have lost the pointer — non-fatal
    }

    const onMove = (evt: PointerEvent): void => {
      const hit = geometry.pointToTargetIndex(evt.clientX, evt.clientY);
      this._ghost.value = { clientX: evt.clientX, clientY: evt.clientY };
      if (!hit) {
        // Pointer crossed a gap (between columns, outside any card
        // list). Clearing targetIndex would reset `liveShifts$` to an
        // empty map, which flips the card's `reorderBusy` off, drops
        // transition to `none` and snaps every card back to its home
        // slot. The next valid hit then animates home→new-slot instead
        // of old-slot→new-slot — user sees a jarring bounce through
        // the origin on every cross-column move. Keep the last valid
        // targetIndex + dropScope so the preview is "sticky": cards
        // stay at their last previewed position until a new valid
        // target takes over. The pointer still tracks for the ghost
        // preview via the `_ghost` signal above.
        return;
      }
      this._state.value = {
        draggedIds,
        targetIndex: hit.index,
        dropScope: hit.scope,
      };
    };

    const finish = (commit: boolean): void => {
      const snapshot = this._state.value;
      if (!commit) {
        this.teardown();
        onCancel?.();
        return;
      }
      // Move rows FIRST, THEN clear the reorder state. Running both
      // synchronously lets Lit batch the `rows$` and `state$` signal
      // changes into a single render tick: the bar's new `top`
      // (from the moved rowIndex) and the cleared `translateY` (from
      // the empty rowShifts map) are committed atomically, so the
      // element stays visually at the already-previewed target
      // position — no second animation, no snap-back.
      if (snapshot && snapshot.targetIndex !== null) {
        const position = resolveInsertPosition(
          host.getRowIdsInOrder(snapshot.dropScope),
          snapshot.draggedIds,
          snapshot.targetIndex
        );
        if (position !== null) {
          host.moveRows([...snapshot.draggedIds], position, snapshot.dropScope);
        }
      }
      this.teardown();
      if (snapshot) onCommit?.(snapshot);
    };

    const onUp = (): void => finish(true);
    const onCancelPointer = (): void => finish(false);
    const onKey = (evt: KeyboardEvent): void => {
      if (evt.key === 'Escape') finish(false);
    };
    // Right-click (or any non-left-button press) during a drag
    // aborts: cancel the commit AND swallow the synthetic
    // `contextmenu` event so we don't open a browser menu on top of
    // an already-teared-down reorder. Capture-phase so we reach the
    // menu before the view's own contextmenu handlers.
    const onContextMenu = (evt: Event): void => {
      evt.preventDefault();
      evt.stopPropagation();
      finish(false);
    };
    const onPointerDown = (evt: PointerEvent): void => {
      if (evt.button !== 0) finish(false);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancelPointer);
    window.addEventListener('keydown', onKey);
    window.addEventListener('contextmenu', onContextMenu, true);
    window.addEventListener('pointerdown', onPointerDown, true);

    this.cleanup = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancelPointer);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('contextmenu', onContextMenu, true);
      window.removeEventListener('pointerdown', onPointerDown, true);
      try {
        captureTarget?.releasePointerCapture(pointer.pointerId);
      } catch {
        // pointer capture may already be gone — non-fatal
      }
    };
  }

  cancel(): void {
    if (!this.cleanup) return;
    this.teardown();
  }

  private teardown(): void {
    this.cleanup?.();
    this.cleanup = null;
    this._state.value = null;
    this._ghost.value = null;
    this._geometry.value = null;
  }

  private resolveDraggedIds(
    seedRowId: string,
    selectedIds: ReadonlySet<string>
  ): ReadonlySet<string> {
    if (selectedIds.has(seedRowId) && selectedIds.size > 1) {
      return new Set(selectedIds);
    }
    return new Set([seedRowId]);
  }
}

/**
 * Resolve the {@link InsertToPosition} for a move given the live row
 * order, the set of ids being dragged, and the hovered target index.
 * Dragged rows at or after the target are skipped so the anchor is
 * always a stationary row; if no stationary row remains, the move
 * lands at `'end'`.
 */
export function resolveInsertPosition(
  order: readonly string[],
  draggedIds: ReadonlySet<string>,
  targetIndex: number
): InsertToPosition | null {
  if (order.length === 0) return 'end';
  const clamped = Math.max(0, Math.min(order.length, targetIndex));
  for (let i = clamped; i < order.length; i++) {
    const id = order[i];
    if (id !== undefined && !draggedIds.has(id)) {
      return { id, before: true };
    }
  }
  return 'end';
}
